// The Claude tasks the app runs, with their instructions and result schemas. The browser sends them to
// POST /api/ai, which adds the API key. Claude reads and classifies; it never writes messages for Peter.

import type { Deal } from "./network";
import { DEAL_TYPES, SECTORS } from "./network";

export interface AiTask {
  task: string;
  system: string;
  schema: object;
  effort: "low" | "medium" | "high";
}

export const TEASER_TASK: AiTask = {
  task: "teaser",
  effort: "medium",
  system: [
    "You read a deal teaser or sponsor email that a lower-middle-market capital raiser received, and pull out the facts he screens on.",
    "Use only what the text says. When a fact is missing, use the empty value (empty string, 0, false, or an empty list) rather than guessing.",
    `Sectors must come from this list: ${SECTORS.join("; ")}. "adjacent" is up to three related sectors from the same list that investors in the main sectors also like.`,
    `structure must be one of: ${DEAL_TYPES.join("; ")}, or empty.`,
    "deal_type is a short phrase like 'Carve-out buyout', 'Growth equity co-invest', 'Real estate development', 'Seed round' or 'Fund'.",
    "raise and min_ticket are in US dollars. timeline_months is how long the sponsor gives to raise the money.",
    "sponsor_prior_raises counts deals or funds the sponsor has raised capital for before. retainer_offered is true only if a retainer or monthly fee is offered.",
    "values_flags lists anything that could conflict with a conservative investor's values (for example cannabis, gambling, adult content, litigation finance); empty if none.",
    "one_liner is a plain description of the business in under 25 words, written the way you'd say it out loud.",
  ].join("\n"),
  schema: {
    type: "object",
    additionalProperties: false,
    required: ["codename", "one_liner", "summary", "sponsor", "deal_type", "structure", "sectors", "adjacent", "raise", "min_ticket", "region", "timeline_months", "sponsor_prior_raises", "retainer_offered", "is_fund", "first_time_fund", "values_flags"],
    properties: {
      codename: { type: "string" },
      one_liner: { type: "string" },
      summary: { type: "string" },
      sponsor: { type: "string" },
      deal_type: { type: "string" },
      structure: { type: "string" },
      sectors: { type: "array", items: { type: "string" } },
      adjacent: { type: "array", items: { type: "string" } },
      raise: { type: "number" },
      min_ticket: { type: "number" },
      region: { type: "string" },
      timeline_months: { type: "number" },
      sponsor_prior_raises: { type: "number" },
      retainer_offered: { type: "boolean" },
      is_fund: { type: "boolean" },
      first_time_fund: { type: "boolean" },
      values_flags: { type: "array", items: { type: "string" } },
    },
  },
};

export type TeaserResult = Omit<Deal, "deal_id" | "received" | "culture_gut" | "friend_gp">;

/** Turns Claude's reading of a teaser into a Deal Peter can review before saving. */
export function dealFromTeaser(r: Partial<TeaserResult>, today: string): Deal {
  const inList = (xs: unknown, list: string[]) => (Array.isArray(xs) ? xs.filter((x): x is string => typeof x === "string" && list.includes(x)) : []);
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0);
  const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const codename = str(r.codename) || "New deal";
  return {
    deal_id: "D-" + codename.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24) + "-" + Date.now().toString(36).slice(-4).toUpperCase(),
    codename,
    received: today,
    one_liner: str(r.one_liner),
    summary: str(r.summary),
    sponsor: str(r.sponsor),
    deal_type: str(r.deal_type),
    structure: DEAL_TYPES.includes(str(r.structure)) ? str(r.structure) : "",
    sectors: inList(r.sectors, SECTORS),
    adjacent: inList(r.adjacent, SECTORS).slice(0, 3),
    raise: num(r.raise),
    min_ticket: num(r.min_ticket) || 250000,
    region: str(r.region),
    timeline_months: num(r.timeline_months) || 12,
    sponsor_prior_raises: num(r.sponsor_prior_raises),
    retainer_offered: r.retainer_offered === true,
    first_time_fund: r.first_time_fund === true,
    friend_gp: false,
    is_fund: r.is_fund === true,
    values_flags: Array.isArray(r.values_flags) ? r.values_flags.filter((x): x is string => typeof x === "string").slice(0, 6) : [],
    culture_gut: 3,
  };
}

/** An empty deal for the manual form. */
export function blankDeal(today: string): Deal {
  return dealFromTeaser({}, today);
}
